/**
 * Offline-First Service
 * Handles local caching and sync for Service_Delivery and Incident_Reporting
 */

const OFFLINE_CACHE_KEY = 'offline_cache';
const SYNC_QUEUE_KEY = 'sync_queue';

/**
 * Cache data locally for offline access
 * @param {string} resourceType - Resource type (service_delivery, incident_reporting)
 * @param {object} data - Data to cache
 * @returns {object} Cache result
 */
export const cacheData = (resourceType, data) => {
  try {
    const cache = JSON.parse(localStorage.getItem(OFFLINE_CACHE_KEY) || '{}');
    
    if (!cache[resourceType]) {
      cache[resourceType] = [];
    }
    
    // Add timestamp and sync status
    const cachedItem = {
      ...data,
      cachedAt: new Date().toISOString(),
      synced: false,
      syncAttempts: 0
    };
    
    cache[resourceType].push(cachedItem);
    localStorage.setItem(OFFLINE_CACHE_KEY, JSON.stringify(cache));
    
    // Add to sync queue
    addToSyncQueue(resourceType, cachedItem.id || Date.now().toString());
    
    return { success: true, cachedItem };
  } catch (error) {
    console.error('Error caching data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get cached data
 * @param {string} resourceType - Resource type
 * @returns {array} Cached items
 */
export const getCachedData = (resourceType) => {
  try {
    const cache = JSON.parse(localStorage.getItem(OFFLINE_CACHE_KEY) || '{}');
    return cache[resourceType] || [];
  } catch (error) {
    console.error('Error retrieving cached data:', error);
    return [];
  }
};

/**
 * Add item to sync queue
 * @param {string} resourceType - Resource type
 * @param {string} itemId - Item ID
 */
const addToSyncQueue = (resourceType, itemId) => {
  try {
    const queue = JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY) || '[]');
    queue.push({
      resourceType,
      itemId,
      queuedAt: new Date().toISOString(),
      attempts: 0
    });
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(queue));
  } catch (error) {
    console.error('Error adding to sync queue:', error);
  }
};

/**
 * Check if device is online
 * @returns {boolean}
 */
export const isOnline = () => {
  return navigator.onLine;
};

/**
 * Sync cached data when online
 * @param {function} syncFunction - Function to call for syncing
 * @returns {object} Sync result
 */
export const syncCachedData = async (syncFunction) => {
  if (!isOnline()) {
    return { success: false, error: 'Device is offline' };
  }

  try {
    const queue = JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY) || '[]');
    const cache = JSON.parse(localStorage.getItem(OFFLINE_CACHE_KEY) || '{}');
    
    const results = [];
    
    for (const queueItem of queue) {
      const { resourceType, itemId } = queueItem;
      const cachedItems = cache[resourceType] || [];
      const item = cachedItems.find(i => (i.id || i.timestamp) === itemId);
      
      if (item && !item.synced) {
        try {
          const syncResult = await syncFunction(resourceType, item);
          if (syncResult.success) {
            item.synced = true;
            item.syncedAt = new Date().toISOString();
            results.push({ success: true, itemId, resourceType });
          } else {
            item.syncAttempts = (item.syncAttempts || 0) + 1;
            results.push({ success: false, itemId, resourceType, error: syncResult.error });
          }
        } catch (error) {
          item.syncAttempts = (item.syncAttempts || 0) + 1;
          results.push({ success: false, itemId, resourceType, error: error.message });
        }
      }
    }
    
    // Update cache
    localStorage.setItem(OFFLINE_CACHE_KEY, JSON.stringify(cache));
    
    // Remove synced items from queue
    const remainingQueue = queue.filter(q => {
      const item = cache[q.resourceType]?.find(i => (i.id || i.timestamp) === q.itemId);
      return !item?.synced;
    });
    localStorage.setItem(SYNC_QUEUE_KEY, JSON.stringify(remainingQueue));
    
    return {
      success: true,
      synced: results.filter(r => r.success).length,
      failed: results.filter(r => !r.success).length,
      results
    };
  } catch (error) {
    console.error('Error syncing cached data:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get sync status
 * @returns {object} Sync status
 */
export const getSyncStatus = () => {
  try {
    const queue = JSON.parse(localStorage.getItem(SYNC_QUEUE_KEY) || '[]');
    const cache = JSON.parse(localStorage.getItem(OFFLINE_CACHE_KEY) || '{}');
    
    let totalCached = 0;
    let totalSynced = 0;
    let totalPending = 0;
    
    Object.keys(cache).forEach(resourceType => {
      const items = cache[resourceType] || [];
      totalCached += items.length;
      totalSynced += items.filter(i => i.synced).length;
      totalPending += items.filter(i => !i.synced).length;
    });
    
    return {
      isOnline: isOnline(),
      totalCached,
      totalSynced,
      totalPending,
      queueLength: queue.length
    };
  } catch (error) {
    console.error('Error getting sync status:', error);
    return null;
  }
};

export default {
  cacheData,
  getCachedData,
  isOnline,
  syncCachedData,
  getSyncStatus
};

